import React, { useEffect } from 'react';
import { navigate } from 'gatsby';
import {
  translatePage,
  useTranslation,
} from '@ikea-customer-self-serve/gatsby-plugin-translations';
import Loading, { LoadingBall } from '@ingka/loading';
import { Divider } from '../styles';
import { Item } from '../components/Item';
import SiteMetadata from '../components/SiteMetadata';
import { getURLParameters } from '../helpers/url';
import { StatusType } from '../interfaces/Cart';
import { useConfirm } from '../hooks/useConfirm';
import TMarkUp from '../components/TMarkUp';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  InformationMessage,
  StyledInformation,
  DetailsSection,
  SubTotals,
  Total,
  FlexContainer,
  SummaryLeftCol,
  SummaryRightCol,
  Wrapper,
  SummaryDivider,
  DeliveryStatusText,
  StyledStatus,
  DeliveryStatus,
} from '../styles/status';

interface StatusProps {
  status: StatusType;
}

const StatusColor: React.FC<StatusProps> = ({ status }): JSX.Element => {
  const statusColor = status === 'CANCELLED' ? 'orange' : 'green';
  const { t } = useTranslation();
  return (
    <StyledStatus
      statusColor={statusColor}
      label={t(`spareParts.summary.status.${status}`)}
      statusDotPosition="trailing"
    />
  );
};

const ConfirmPageComponent = (): JSX.Element | null => {
  const { t } = useTranslation();
  const crumbs = [{ url: '/confirm', text: t('seoContent.page.status') }];
  const title = t('seoContent.page.summary');
  const order = getURLParameters('order') as string;
  const token = getURLParameters('token') as string;
  const { status, items, loading } = useConfirm(order, token);

  useEffect(() => {
    if (!loading) {
      if (items.length === 0) {
        navigate('/');
      } else if (typeof window !== 'undefined') {
        window.history.replaceState('', '', window.location.pathname);
      }
    }
  }, [items, loading]);

  if (loading) {
    return (
      <Loading
        text={t('loading', {
          loading: t(`spareParts.sparePart`),
        })}
      >
        <LoadingBall />
      </Loading>
    );
  }

  if (!items.length) {
    return null;
  }

  const nbrOfItemsText =
    items.length === 1
      ? t('spareParts.summary.items.singular')
      : t('spareParts.summary.items.plural');

  return (
    <>
      <SiteMetadata pageTitle={title} />
      <Breadcrumbs crumbs={crumbs} />
      <FlexContainer>
        <SummaryLeftCol>
          <Wrapper>
            <h1 className="h1">
              <TMarkUp
                value={t('spareParts.summary.title.highlightedConfirmed')}
              />
              <br />
              <TMarkUp value={t('spareParts.summary.title.normal')} />
            </h1>
            <StyledInformation>
              <p>
                <TMarkUp
                  value={t('spareParts.summary.shipping.deliveryTime')}
                />
              </p>
            </StyledInformation>
          </Wrapper>
        </SummaryLeftCol>
        <SummaryDivider />
        <SummaryRightCol>
          <DetailsSection>
            {
              <InformationMessage
                variant={'positive'}
                title={t('spareParts.summary.confirmationAlert.success')}
                body={t('spareParts.summary.confirmationAlert.normal')}
              />
            }
            <DeliveryStatus>
              <DeliveryStatusText>
                <TMarkUp
                  value={`${t('spareParts.summary.delivery.status')}:`}
                />
                <StatusColor status={status} />
              </DeliveryStatusText>
            </DeliveryStatus>
          </DetailsSection>
          <DetailsSection>
            <h3 className="h4">{nbrOfItemsText}</h3>
            <ul>
              {items.map((sparePartItem) => {
                const { item, quantity } = sparePartItem;
                return (
                  <Item item={item} key={item.id}>
                    <TMarkUp
                      value={t('spareParts.summary.items.quantity', {
                        quantity,
                      })}
                    />
                  </Item>
                );
              })}
            </ul>
          </DetailsSection>
          <Divider />
          <SubTotals>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.subtotal')} />
            </span>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.free')} />
            </span>
          </SubTotals>
          <SubTotals>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.shippingCost')} />
            </span>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.free')} />
            </span>
          </SubTotals>
          <Divider thick />
          <Total>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.total')} />
            </span>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.free')} />
            </span>
          </Total>
        </SummaryRightCol>
      </FlexContainer>
    </>
  );
};

export default translatePage()(ConfirmPageComponent);
